<template>
  <figure
    :class="`dn-image ${layout}`"
    :data-load="loadingMode"
  >
    <picture
      ref="image"
      :class="`ratio-${imageLib.getRatio()}`"
    >
      <template v-if="showImage">
        <img
          v-if="imageSrc"
          :alt="alt ? alt : caption"
          :loading="effectiveLoadingMode"
          :src="imageSrc"
          :fetchpriority="fetchPriority"
          decoding="async"
        >
      </template>
    </picture>
    <figcaption v-if="caption || credit">
      <span
        v-if="caption"
        class="caption-below-image"
      >
        {{ caption }}
      </span>
      <span
        v-if="credit"
        class="credit"
      >
        {{ credit }}
      </span>
    </figcaption>
  </figure>
</template>
<script setup lang="ts">
import { ref, nextTick, type PropType } from 'vue';
import debounce from 'lodash.debounce';
import type { PointOfInterest } from '~/types/elements';
import type { PublicationContext } from '~/types';

const rtConfPub = useRuntimeConfig().public as unknown as PublicationContext;

const props = defineProps({
  formats: {
    type: Array as PropType<string[]>,
    required: false,
    default: () => ['webp', 'jpeg']
  },
  /**
   * Fallback src when sources aren't provided, or doesn't match
   * @param {String} value = imageUrl
   */
  src: {
    type: String,
    required: false,
    default: null
  },
  imgKey: {
    type: String,
    required: false,
    default: null
  },
  pointOfInterest: {
    type: Object as PropType<PointOfInterest>,
    required: false,
    default: () => {}
  },
  matchAreaOfInterest: {
    type: Boolean,
    required: false,
    default: false
  },
  showImage: {
    type: Boolean,
    required: false,
    default: false
  },
  imageCrops: {
    type: Object,
    required: false,
    default: () => {}
  },
  width: {
    type: Number,
    required: false,
    default: 980
  },
  /**
   * Image short description
   * @param {String} value = come text
   */
  alt: {
    type: String,
    required: false,
    default: null
  },

  /**
   * Property to enable lazyload for the image
   * @param {String} value = ['lazy', 'eager']
   */
  loadingMode: {
    type: String as PropType<'lazy' | 'eager'>,
    required: false,
    default: 'lazy'
  },
  /**
   * Property to prioritize image fetch
   * @param {String} value = ['high', 'low']
   */
  fetchPriority: {
    type: String as PropType<'high' | 'low' | 'auto'>,
    required: false,
    default: 'low'
  },
  /**
   * Image ratio
   * @param {String} value = 3x2
   */
  ratio: {
    type: [String, Array] as PropType<string | string[]>,
    required: false,
    default: '3x2'
  },
  /**
   * Layout full or wide
   * @param {String} value = 'default'
   */
  layout: {
    type: String,
    default: 'default'
  },
  /**
   * Image caption
   * @param {String} value = ''
   */
  caption: {
    type: String,
    required: false,
    default: null
  },
  /**
   * Image credit
   * @param {String} value = ''
   */
  credit: {
    type: String,
    required: false,
    default: null
  },
  originalWidth: {
    type: Number,
    required: true,
    default: 0
  },
  originalHeight: {
    type: Number,
    required: true,
    default: 0
  }
});
const image = ref<HTMLElement | null>(null);
const fp = computed(() => props.pointOfInterest?.centerX && props.pointOfInterest?.centerY ? [props.pointOfInterest.centerX, props.pointOfInterest.centerY] : []);
const imageLib = useImage(props.src, {
  fp: fp.value,
  poiData: props.pointOfInterest,
  imageCrops: props.imageCrops,
  ratio: props.ratio || '3x2',
  isMatchAoi: props.matchAreaOfInterest,
  originalWidth: props.originalWidth,
  originalHeight: props.originalHeight
});
const imgWidth = ref(props.width);
const imgHeight = ref(imageLib.getHeightByRatio(imgWidth.value));
const showImage = ref<boolean>(props.showImage);
const debouncedHandleResize = debounce(handleResize, 500);
const isBackNavigation = ref(false);

const imageSrc = computed(() => {
  const imageLib = useImage(props.src, {
    fp: fp.value,
    poiData: props.pointOfInterest,
    imageCrops: props.imageCrops,
    ratio: props.ratio || '3x2',
    isMatchAoi: props.matchAreaOfInterest,
    originalWidth: props.originalWidth,
    originalHeight: props.originalHeight
  });
  return imageLib.getImageSrc(rtConfPub.imageFormat || 'auto', imgWidth.value, imgHeight.value);
});

function handleResize () {
  const pixelRatio = window.devicePixelRatio || 1;
  imgWidth.value = (image.value?.offsetWidth || image.value?.clientWidth || window.innerWidth) * pixelRatio;
  imgHeight.value = (image.value?.offsetHeight || image.value?.clientHeight || window.innerHeight) * pixelRatio;
  if (!showImage.value) {
    showImage.value = true; // Show image when sources are ready
  }
}
const effectiveLoadingMode = computed(() => {
  return isBackNavigation.value ? 'eager' : props.loadingMode;
});

onMounted(() => {
  if ('ResizeObserver' in window) {
    useResizeObserver(image, debouncedHandleResize);
  } else {
    // Fallback: use window resize for older browsers
    useEventListener('resize', debouncedHandleResize);
  }
  nextTick(() => {
    requestAnimationFrame(handleResize);
  });

  if (window.history.state && window.history.state.scroll) {
    isBackNavigation.value = true;
  }

  // Listen for popstate events (back/forward button)
  useEventListener('popstate', () => {
    isBackNavigation.value = true;
  });
});
</script>
<style lang="scss">
.dn-image {
  --_dn-image_border-radius: var(--dn-image_border-radius, var(--box-radius));
  --_dn-image_height: var(--dn-image_height, auto);
  --_dn-image_background: var(--dn-image_background, var(--color-brand-utility-50));
  --_image-figcaption-color: var(--image-figcaption-color, var(--color-typography-default));
  --_image-figcaption-inline-size: var(--image-figcaption-inline-size, 100%);
  --_image-figcaption-credit-before-content: var(--image-figcaption-credit-prefix, '(');
  --_image-figcaption-credit-after-content: var(--image-figcaption-credit-after-content, ')');

  width: 100%;
  height: var(--_dn-image_height);
  padding: 0;
  margin: 0;

  picture {
    --_dn-image_aspect-ratio: var(--dn-image_aspect-ratio, auto);

    display: flex;
    aspect-ratio: var(--_dn-image_aspect-ratio);
    justify-content: center;
    background: var(--_dn-image_background);
    border-radius: var(--_dn-image_border-radius);
    overflow: hidden;

    &.ratio-5x2 {
      --dn-image_aspect-ratio: 5 / 2;
    }

    &.ratio-2x1 {
      --dn-image_aspect-ratio: 2 / 1;
    }

    &.ratio-3x1 {
      --dn-image_aspect-ratio: 3 / 1;
    }

    &.ratio-4x1 {
      --dn-image_aspect-ratio: 4 / 1;
    }

    &.ratio-16x9 {
      --dn-image_aspect-ratio: 16 / 9;
    }

    &.ratio-16x10 {
      --dn-image_aspect-ratio: 16 / 10;
    }

    &.ratio-3x2 {
      --dn-image_aspect-ratio: 3 / 2;
    }

    &.ratio-2x3 {
      --dn-image_aspect-ratio: 2 / 3;
    }

    &.ratio-4x3 {
      --dn-image_aspect-ratio: 4 / 3;
    }

    &.ratio-3x4 {
      --dn-image_aspect-ratio: 3 / 4;
    }

    &.ratio-1x1 {
      --dn-image_aspect-ratio: 1 / 1;
    }

    &.ratio-5x6 {
      --dn-image_aspect-ratio: 5 / 6;
    }

    &.ratio-5x7 {
      --dn-image_aspect-ratio: 5 / 7;
    }

    &.fullheight {
      --dn-image_aspect-ratio: auto;

      height: 100%;
    }

    &.ratio-auto {
      --dn-image_aspect-ratio: auto;

      height: 100%;
    }

    img,
    video {
      width: 100%;
      object-fit: cover;
    }
  }

  figcaption {
    display: flex;
    flex-wrap: wrap;
    column-gap: 8px;
    width: 100%;
    font-family: var(--typography-utility-s-font-family);
    font-size: var(--typography-utility-s-font-size);
    font-weight: var(--typography-utility-s-font-weight);
    line-height: 140%;
    inline-size: min(var(--page-width), 100vw - calc(var(--site-padding) * 2));
    margin-inline: auto;
    padding: var(--ui-gap) 0;
    text-align: left;
    padding-top: 12px;
    max-width: 100%;
    color: var(--color-typography-secondary);

    @media only screen and (width >= 768px) {
      font-family: var(--typography-utility-m-font-family);
      font-size: var(--typography-utility-m-font-size);
      font-weight: var(--typography-utility-m-font-weight);
      padding-top: 16px;
      inline-size: min(var(--grid-width), 100vw - calc(var(--site-padding) * 2));
    }

    .credit {
      color: var(--color-neutral-700);

      &::before {
        content: var(--_image-figcaption-credit-before-content);
      }

      &::after {
        content: var(--_image-figcaption-credit-after-content);
      }
    }
  }

  &.wide,
  &.center {
    @media only screen and (width >= 980px) {
      max-width: var(--grid-width);
    }
  }

  &.full {
    @media only screen and (width >= 980px) {
      max-width: var(--page-width);
    }
  }

  &.small,
  &.free {
    max-width: var(--content-width);
    display: flex;
    justify-content: flex-start;
    align-items: flex-end;
    flex-direction: row;

    picture {
      flex: 0 0 340px;
      max-width: 40%;
    }

    figcaption {
      display: flex;
      flex-direction: column;
      text-align: left;
      padding: 0 0 0 var(--row-gap);
      margin: 0;

      .credit {
        &::before,
        &::after {
          padding: 0;
          padding-left: 0px;
        }
      }
    }
  }
}
</style>
